import React from 'react'

import type { hasVoice } from '@/app/module/campaigns/message-type-helpers'
import { supportsCampaignQuotas } from '@/app/module/campaigns/quota-helpers'
import { CampaignTemplateType } from '@/app/module/campaigns/types'

import AudioFilesContainer from './item/steps/audio-files/audio-files.container'
import CallWindow from './item/steps/call-window/call-window'
import CatiSettings from './item/steps/cati-settings/cati-settings'
import Confirm from './item/steps/confirm'
import Content from './item/steps/content'
import DefaultEventTimeContainer from './item/steps/event-time/default-event-time.container'
import LanguagesContainer from './item/steps/languages/language-configuration.container'
import PhoneNumberContainer from './item/steps/phone/phone-number.container'
import QuotasContainer from './item/steps/quotas/quotas.container'
import ReconnectContainer from './item/steps/reconnect/reconnect.container'
import RetryContainer from './item/steps/retry/retry.container'
import VoicemailContainer from './item/steps/voicemail/voicemail.container'

type GetCampaignTypeParams = {
  hasLanguage: boolean
  hasVoiceMessage: boolean | ReturnType<typeof hasVoice>
  isReminder: boolean
  published: boolean
  type: CampaignTemplateType
}

export type StepConfig = {
  name: string
  title: string
  icon: string
  saveButtonLabel: string
  submitButtonLabel: string
  Component: React.ComponentType<any>
  disabled?: boolean
}

export const STEP_MAP = [
  'languages',
  'event-time',
  'content',
  'quotas',
  'audio-files',
  'number',
  'reconnect',
  'retry',
  'voicemail',
  'call-windows',
  'cati-settings',
  'confirm',
]

export const isCampaignEditPath = ({ pathname }: { pathname: string }): boolean =>
  /^\/campaigns\/edit(?=\/|$)/.test(pathname)

export const getCampaignSteps = ({
  hasLanguage,
  hasVoiceMessage,
  isReminder,
  published,
  type,
}: GetCampaignTypeParams) => {
  let phoneNumberName = 'Phone Numbers'
  if (type === 'smsblast') {
    phoneNumberName = 'Sender ID'
  } else if (type === 'ivr') {
    phoneNumberName = 'Caller IDs, etc.'
  } else if (['cati', 'drip'].includes(type)) {
    phoneNumberName = 'Sender IDs, etc.'
  }
  const allSteps: Record<string, StepConfig> = {
    languages: {
      name: 'languages',
      title: 'Languages',
      icon: 'language',
      saveButtonLabel: 'Save',
      submitButtonLabel: 'Next',
      Component: LanguagesContainer,
      disabled: !hasLanguage,
    },
    content: {
      name: 'content',
      title: 'Content',
      icon: 'edit',
      saveButtonLabel: 'Save',
      submitButtonLabel: 'Next',
      Component: Content,
    },
    quotas: {
      name: 'quotas',
      title: 'Quotas',
      icon: 'pie_chart',
      saveButtonLabel: 'Save',
      submitButtonLabel: 'Next',
      Component: QuotasContainer,
      disabled: !supportsCampaignQuotas(type),
    },
    'event-time': {
      name: 'event-time',
      title: 'Event Time',
      icon: 'schedule',
      saveButtonLabel: 'Save',
      submitButtonLabel: 'Next',
      Component: DefaultEventTimeContainer,
      disabled: !isReminder,
    },
    number: {
      name: 'number',
      title: phoneNumberName,
      icon: 'ad_units',
      saveButtonLabel: 'Save',
      submitButtonLabel: 'Next',
      Component: PhoneNumberContainer,
    },
    reconnect: {
      name: 'reconnect',
      title: 'Reconnect Calls',
      icon: 'phone',
      saveButtonLabel: 'Save',
      submitButtonLabel: 'Next',
      Component: ReconnectContainer,
      disabled: type !== 'ivr',
    },
    retry: {
      name: 'retry',
      title: 'Retry Calls',
      icon: 'phone',
      saveButtonLabel: 'Save',
      submitButtonLabel: 'Next',
      Component: RetryContainer,
      disabled: !hasVoiceMessage,
    },
    'audio-files': {
      name: 'audio-files',
      title: 'Audio Files',
      icon: 'library_music',
      saveButtonLabel: 'Save',
      submitButtonLabel: 'Next',
      Component: AudioFilesContainer,
      disabled: !(type === 'ivr' || (type === 'drip' && hasVoiceMessage)),
    },
    voicemail: {
      name: 'voicemail',
      title: 'Voicemail Detection',
      icon: 'phone',
      saveButtonLabel: 'Save',
      submitButtonLabel: 'Next',
      Component: VoicemailContainer,
      disabled: !hasVoiceMessage,
    },
    'call-windows': {
      name: 'call-windows',
      title: 'Call Windows',
      icon: 'phone',
      saveButtonLabel: 'Save',
      submitButtonLabel: 'Next',
      Component: CallWindow,
      disabled: !hasVoiceMessage,
    },
    'cati-settings': {
      name: 'cati-settings',
      title: 'CATI Settings',
      icon: 'settings',
      saveButtonLabel: 'Save',
      submitButtonLabel: 'Next',
      Component: CatiSettings,
      disabled: type !== 'cati',
    },
    confirm: {
      name: 'confirm',
      title: published ? 'Save Updates' : 'Confirm & Set Live',
      icon: 'check',
      saveButtonLabel: `Save${published ? ' & Close' : ''}`,
      submitButtonLabel: 'Set Live',
      Component: Confirm,
    },
  }

  return {
    stepsMap: STEP_MAP,
    steps: STEP_MAP.reduce<Record<number, StepConfig>>(
      (acc, key, index) => ({
        ...acc,
        [index]: allSteps[key],
      }),
      {},
    ),
  }
}
